import { useEffect } from "react";

// One mechanism for unsaved form changes across every page, instead of a different one per module.
//
// A page is "dirty" once the user types, picks or ticks something inside its content (a region wrapped with
// useUnsavedTracking), and clean again after the next successful save request (api.js reports every
// successful POST/PUT/PATCH/DELETE here) or after the user agrees to leave. Leaving a dirty page — another
// engagement panel, a sidebar link, the drawer, a firm-level page, closing or reloading the tab — asks first.
// Fields that are navigation rather than data (menu search, filters) carry data-dirty-ignore.
//
// Editors that know exactly what is still unconfirmed (the KKA editor: a refused save, a queued edit, a conflict)
// hold the page dirty under their own owner token instead (hold/release). A successful write elsewhere on the page
// — an evidence upload or review, another panel's save — clears only the page-level flag, never a holder's edits;
// a holder releases itself when its edits are confirmed or discarded, or when it unmounts (B-517).
//
// Known limit: the browser Back/Forward buttons inside the single-page app are not intercepted (the app uses
// BrowserRouter, which has no navigation blocker); a reload or tab close still asks.

let dirty = false;
const owners = new Set();
const listeners = new Set();
const notify = () => { const state = isDirty(); listeners.forEach((fn) => fn(state)); };
const changing = (change) => { const before = isDirty(); change(); if (before !== isDirty()) notify(); };

export const MESSAGE = "Ada perubahan yang belum disimpan di halaman ini. Tinggalkan tanpa menyimpan?";

export function isDirty() { return dirty || owners.size > 0; }
export function markDirty() { changing(() => { dirty = true; }); }
// Called by api.js after every successful write: clears page-level edits only, not what an owner still holds.
export function clearDirty() { changing(() => { dirty = false; }); }
export function hold(owner) { changing(() => owners.add(owner)); }
export function release(owner) { changing(() => owners.delete(owner)); }
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Returns true when it is fine to leave. Leaving discards the page-level changes; a holder's edits are discarded
// when it unmounts, which the navigation that follows does.
export function confirmLeave() {
  if (!isDirty()) return true;
  const ok = window.confirm(MESSAGE);
  if (ok) clearDirty();
  return ok;
}

const IGNORED_TYPES = new Set(["search", "button", "submit", "reset", "file"]);

export function isDataField(target) {
  if (!target || !target.closest) return false;
  if (target.closest("[data-dirty-ignore]")) return false;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return !target.disabled && !target.readOnly;
  if (tag === "INPUT") return !IGNORED_TYPES.has((target.type || "").toLowerCase()) && !target.disabled && !target.readOnly;
  return !!target.isContentEditable;
}

export function useUnsavedTracking(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onEdit = (event) => { if (isDataField(event.target)) markDirty(); };
    el.addEventListener("input", onEdit);
    el.addEventListener("change", onEdit);
    return () => { el.removeEventListener("input", onEdit); el.removeEventListener("change", onEdit); };
  }, [ref]);
  useEffect(() => {
    const onUnload = (event) => { if (isDirty()) { event.preventDefault(); event.returnValue = MESSAGE; return MESSAGE; } return undefined; };
    window.addEventListener("beforeunload", onUnload);
    return () => { window.removeEventListener("beforeunload", onUnload); clearDirty(); };
  }, []);
}
